import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Table,
  Modal,
  Form,
  InputGroup,
  Pagination,
  Spinner,
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserClock,
  faCoffee,
  faUtensils,
  faStopwatch,
  faFileCsv,
  faHistory,
  faPlay,
  faStop,
  faCheckCircle,
  faExclamationTriangle,
  faCalendarAlt,
  faClock,
  faSearch,
  faEye,
  faTimes,
  faDesktop,
  faTrash,
  faLaptop,
  faLock,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";
import { useHistory, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import { activateGeod } from "../../Store";
import { Routes } from "../../routes";
import { postShiftEnd } from "../../Service/AllActivityTrackerAPI";
import {
  ActivityTrackerProvider,
  useActivityTracker,
  formatDuration,
  formatDurationHuman,
  formatBreakTimeMinutes,
  parseScreenActiveTimeToSeconds,
  parseDateSafe,
  formatTimeStr,
  formatDateStr,
  getIsoDateStr,
  isDateInRange,
} from "./ActivityTrackerContext";
import { TaskManagementProvider } from "./TaskManagementContext";
import EmployeeTaskManagement from "./EmployeeTaskManagement";
import "./ActivityTracker.css";

// Inner component that consumes ActivityTrackerContext
const EmployeeActivityTrackerContent = (props) => {
  const history = useHistory();
  const location = useLocation();

  const {
    employee,
    status,
    activeSeconds,
    inactiveSeconds,
    breakSeconds,
    screenLockSeconds,
    sessionSeconds,
    currentBreak,
    sessionStartTime,
    attendanceHistory,
    breakHistory,
    activityLogs,
    isShiftActive,
    isShiftEndedToday,
    todayCompletedRecord,
    startShift,
    endShift,
    startBreak,
    endBreak,
    exportAttendanceCSV,
    exportActivityLogsCSV,
    clearActivityLogs,
    toasts,
    dismissToast,
    addToast,
    systemTrackingEnabled,
    requestSystemIdleTracking,
    fetchAttendanceHistory,
    isLoadingAttendance,
  } = useActivityTracker();

  // Active navigation tab: 'dashboard' | 'attendance' | 'logs' | 'breaks'
  const [activeTab, setActiveTab] = useState(() => {
    // if (location.pathname.includes("tasks")) return "tasks";
    if (location.pathname.includes("attendance")) return "attendance";
    if (location.pathname.includes("logs")) return "logs";
    if (location.pathname.includes("breaks")) return "breaks";
    return "dashboard";
  });

  // Modal & Shift states
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [showPersonalBreakModal, setShowPersonalBreakModal] = useState(false);
  const [selectedBreakType, setSelectedBreakType] = useState("Tea Break");
  const [breakReason, setBreakReason] = useState("Tea & Refreshment");
  const [personalBreakReason, setPersonalBreakReason] = useState("");
  const [personalBreakDuration, setPersonalBreakDuration] = useState("15 min");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedAttendanceRecord, setSelectedAttendanceRecord] = useState(null);
  const [isStartingShift, setIsStartingShift] = useState(false);
  const [isEndingShift, setIsEndingShift] = useState(false);
  const [isStartingBreak, setIsStartingBreak] = useState(false);

  // Live ticking state for real-time 1-second display of working hours from first login until shift end
  const [currentLiveNow, setCurrentLiveNow] = useState(() => Date.now());
  useEffect(() => {
    if (!isShiftActive) return;
    const interval = setInterval(() => {
      setCurrentLiveNow(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [isShiftActive]);

  // Current Working Hours: strictly measured from first login/shift start time until end shift (or current live time)
  const currentWorkingSeconds = useMemo(() => {
    if (isShiftEndedToday) {
      if (todayCompletedRecord?.loginTime && todayCompletedRecord?.logoutTime && todayCompletedRecord.logoutTime !== "--" && todayCompletedRecord.logoutTime !== "0") {
        const loginD = parseDateSafe(todayCompletedRecord.loginTime) || parseDateSafe(todayCompletedRecord.rawRecord?.desklogintime);
        const logoutD = parseDateSafe(todayCompletedRecord.logoutTime) || parseDateSafe(todayCompletedRecord.rawRecord?.desklogouttime);
        if (loginD && logoutD) {
          const diff = Math.max(0, Math.floor((logoutD.getTime() - loginD.getTime()) / 1000));
          if (diff > 0) return diff;
        }
      }
      if (todayCompletedRecord?.totalSeconds) {
        return Number(todayCompletedRecord.totalSeconds);
      }
      return sessionSeconds || 0;
    }
    if (isShiftActive) {
      if (sessionStartTime) {
        return Math.max(0, Math.floor((currentLiveNow - sessionStartTime) / 1000));
      }
      return 0;
    }
    return 0;
  }, [isShiftActive, isShiftEndedToday, sessionStartTime, sessionSeconds, todayCompletedRecord, currentLiveNow]);

  // Attendance filter states & pagination
  const [attendanceSearch, setAttendanceSearch] = useState("");
  const [attendanceDateFilter, setAttendanceDateFilter] = useState("ALL");
  const [attendanceSingleDate, setAttendanceSingleDate] = useState(() => getIsoDateStr(Date.now()));
  const [attendanceStartDate, setAttendanceStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return getIsoDateStr(d);
  });
  const [attendanceEndDate, setAttendanceEndDate] = useState(() => getIsoDateStr(Date.now()));
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState("ALL");
  const [attendancePage, setAttendancePage] = useState(1);
  const [attendancePageSize, setAttendancePageSize] = useState(10);

  // Logs filter states & pagination
  const [logTypeFilter, setLogTypeFilter] = useState("ALL");
  const [logDateFilter, setLogDateFilter] = useState("ALL");
  const [logSingleDate, setLogSingleDate] = useState(() => getIsoDateStr(Date.now()));
  const [logStartDate, setLogStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return getIsoDateStr(d);
  });
  const [logEndDate, setLogEndDate] = useState(() => getIsoDateStr(Date.now()));
  const [logSearch, setLogSearch] = useState("");
  const [logsPage, setLogsPage] = useState(1);
  const [logsPageSize, setLogsPageSize] = useState(15);

  // Break history filter states & pagination
  const [breakDateFilter, setBreakDateFilter] = useState("ALL");
  const [breakSingleDate, setBreakSingleDate] = useState(() => getIsoDateStr(Date.now()));
  const [breakStartDate, setBreakStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return getIsoDateStr(d);
  });
  const [breakEndDate, setBreakEndDate] = useState(() => getIsoDateStr(Date.now()));
  const [breakSearch, setBreakSearch] = useState("");
  const [breaksPage, setBreaksPage] = useState(1);
  const [breaksPageSize, setBreaksPageSize] = useState(10);

  // --- Effects (Must be at the top level, in consistent order) ---
  // Sync tab with URL
  useEffect(() => {
    if (location.pathname.includes("tasks")) setActiveTab("tasks");
    else if (location.pathname.includes("attendance")) setActiveTab("attendance");
    else if (location.pathname.includes("logs")) setActiveTab("logs");
    else if (location.pathname.includes("breaks")) setActiveTab("breaks");
    else setActiveTab("dashboard");
  }, [location.pathname]);

  // Sync attendance data with server when switching to attendance tab
  useEffect(() => {
    if (activeTab === "attendance" && typeof fetchAttendanceHistory === "function") {
      fetchAttendanceHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-reset page numbers when search / filter changes
  useEffect(() => {
    setAttendancePage(1);
  }, [attendanceSearch, attendanceDateFilter, attendanceSingleDate, attendanceStartDate, attendanceEndDate, attendanceStatusFilter]);

  useEffect(() => {
    setLogsPage(1);
  }, [logSearch, logTypeFilter, logDateFilter, logSingleDate, logStartDate, logEndDate]);

  useEffect(() => {
    setBreaksPage(1);
  }, [breakSearch, breakDateFilter, breakSingleDate, breakStartDate, breakEndDate]);

  // Automatically keep selectedBreakType aligned with active break while running
  useEffect(() => {
    if ((status === "ON_BREAK" || Boolean(currentBreak)) && currentBreak) {
      const t = (currentBreak.type || "").toLowerCase();
      if (t.includes("tea")) setSelectedBreakType("Tea Break");
      else if (t.includes("lunch")) setSelectedBreakType("Lunch Break");
      else if (t.includes("personal")) setSelectedBreakType("Personal Break");
    }
  }, [status, currentBreak]);

  // --- Derived State & Break Helpers ---
  const todayDateStr = formatDateStr(Date.now());
  const isOnBreak = status === "ON_BREAK" || Boolean(currentBreak);
  const currentActiveBreakType = currentBreak ? (currentBreak.type || "") : "";
  const isTeaActive = isOnBreak && currentActiveBreakType.toLowerCase().includes("tea");
  const isLunchActive = isOnBreak && currentActiveBreakType.toLowerCase().includes("lunch");
  const isPersonalActive = isOnBreak && (currentActiveBreakType.toLowerCase().includes("personal") || (!isTeaActive && !isLunchActive));

  // Determine disabled state: when a break is running, the other two breaks are strictly disabled
  const isTeaDisabled = !isShiftActive || (isOnBreak && !isTeaActive);
  const isLunchDisabled = !isShiftActive || (isOnBreak && !isLunchActive);
  const isPersonalDisabled = !isShiftActive || (isOnBreak && !isPersonalActive);

  // --- Handlers ---
  // Switch break type and set sensible default reason (locked if currently on break)
  const handleSelectBreakType = (type) => {
    if (isOnBreak) return;
    setSelectedBreakType(type);
    if (type === "Tea Break") {
      setBreakReason("Tea & Refreshment");
      setPersonalBreakDuration("15 min");
    } else if (type === "Lunch Break") {
      setBreakReason("Lunch Break");
      setPersonalBreakDuration("30 min");
    } else if (type === "Personal Break") {
      setPersonalBreakReason("");
      setPersonalBreakDuration("15 min");
      setShowPersonalBreakModal(true);
    }
  };

  // Dashboard break starter (Opens input modal when Personal Break is clicked)
  const handleDashboardBreakClick = async () => {
    if (isOnBreak || isStartingBreak) return;
    if (selectedBreakType === "Personal Break") {
      setPersonalBreakReason("");
      setShowPersonalBreakModal(true);
    } else {
      const defaultReason = selectedBreakType === "Tea Break" ? "Tea & Refreshment" : "Lunch Break";
      const durationMin = selectedBreakType === "Tea Break" ? 15 : 30;
      setIsStartingBreak(true);
      try {
        const res = await startBreak(selectedBreakType, defaultReason, durationMin);
        if (!res?.success) {
          console.warn("Break failed to start on server:", res?.message);
        }
      } finally {
        setIsStartingBreak(false);
      }
    }
  };

  // Confirm Personal Break from popup modal
  const handleConfirmPersonalBreak = async () => {
    if (isOnBreak || isStartingBreak) return;
    const finalReason = personalBreakReason.trim() || "Personal Break";
    let finalBreakType = `Personal Break - ${finalReason}`;
    const durationMin = parseInt(String(personalBreakDuration).replace(/\D/g, ""), 10) || 15;
    setIsStartingBreak(true);
    try {
      const res = await startBreak(finalBreakType, finalReason, durationMin);
      // ONLY close modal and clear reason if API succeeded!
      if (res?.success) {
        setShowPersonalBreakModal(false);
        setPersonalBreakReason("");
      }
    } finally {
      setIsStartingBreak(false);
    }
  };

  // Break modal submission with user-entered reason
  const handleStartBreakSubmit = async () => {
    if (isOnBreak || isStartingBreak) return;
    const finalReason = breakReason.trim() || selectedBreakType;
    let finalBreakType = selectedBreakType;
    if (selectedBreakType === "Personal Break" && breakReason.trim()) {
      finalBreakType = `Personal Break - ${breakReason.trim()}`;
    }
    let durationMin = 15;
    if (selectedBreakType === "Lunch Break") {
      durationMin = 30;
    } else if (selectedBreakType === "Tea Break") {
      durationMin = 15;
    } else {
      durationMin = parseInt(String(personalBreakDuration).replace(/\D/g, ""), 10) || 15;
    }
    setIsStartingBreak(true);
    try {
      const res = await startBreak(finalBreakType, finalReason, durationMin);
      // ONLY close modal if API succeeded!
      if (res?.success) {
        setShowBreakModal(false);
      }
    } finally {
      setIsStartingBreak(false);
    }
  };

  // Logout / Shift End confirmed (reserved if dual modal button is re-enabled)
  /*
  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    setIsEndingShift(true);

    const now = new Date();
    const formattedEndTime = moment(now).format("YYYY-MM-DD HH:mm:ss");
    const username = sessionStorage.getItem("userName") || employee?.userName || employee?.username || employee?.id || "user";

    try {
      await postShiftEnd(username, formattedEndTime);
    } catch (error) {
      console.error("shiftEnd API error during logout:", error);
    } finally {
      setIsEndingShift(false);
      endShiftAndLogout(() => {
        // Re-use existing application logout mechanism
        if (typeof props.activateGeod === "function") {
          props.activateGeod({ title: false });
        }
        sessionStorage.clear();
        history.replace(Routes.Signin.path);
      });
    }
  };
  */

  // Start Shift with server synchronization
  const handleStartShift = async (customMorningTime = null) => {
    if (isShiftEndedToday) {
      if (addToast) {
        addToast("warning", "Shift Ended", "Your shift for today has already ended.");
      }
      return;
    }
    setIsStartingShift(true);
    try {
      await startShift(customMorningTime);
    } catch (err) {
      console.error("Error in handleStartShift:", err);
    } finally {
      setIsStartingShift(false);
    }
  };

  // End Shift (Stay on Page) with POST API call
  const handleEndShiftStayOnPage = async () => {
    setShowLogoutModal(false);
    setIsEndingShift(true);

    // Call endShift immediately so local state and localStorage are updated instantaneously!
    endShift(false);

    const now = new Date();
    const formattedEndTime = moment(now).format("YYYY-MM-DD HH:mm:ss");
    const username = sessionStorage.getItem("userName") || employee?.userName || employee?.username || employee?.id || "user";

    try {
      const response = await postShiftEnd(username, formattedEndTime);
      console.log("shiftEnd API success:", response?.data);
      if (addToast) {
        addToast("success", "Shift Ended", "Shift ended successfully on server.");
      }
      if (typeof fetchAttendanceHistory === "function") {
        fetchAttendanceHistory(username).catch(() => { });
      }
    } catch (error) {
      console.error("shiftEnd API error:", error);
      if (addToast) {
        addToast(
          "warning",
          "Shift Ended (Local)",
          "Shift ended locally. Server sync response: " +
          (error?.response?.data?.message || error?.message || "Check network")
        );
      }
    } finally {
      setIsEndingShift(false);
    }
  };


  // Tab change handler with history sync
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "attendance") history.push(Routes.EmployeeAttendanceHistory.path);
    else if (tab === "logs") history.push(Routes.EmployeeActivityLogs.path);
    else if (tab === "breaks") history.push(Routes.EmployeeBreakHistory.path);
    else if (tab === "tasks") history.push(Routes.EmployeeTasks.path);
    else history.push(Routes.EmployeeActivityDashboard.path);
  };

  // Calculate percentage splits
  const totalTracked = Math.max(1, activeSeconds + inactiveSeconds + breakSeconds);
  const activePercent = Math.round((activeSeconds / totalTracked) * 100);
  const breakPercent = Math.round((breakSeconds / totalTracked) * 100);
  const inactivePercent = Math.max(0, 100 - activePercent - breakPercent);

  // Filtered Attendance Records
  const filteredAttendance = attendanceHistory.filter((record) => {
    // Search
    const matchesSearch =
      record.date.toLowerCase().includes(attendanceSearch.toLowerCase()) ||
      record.status.toLowerCase().includes(attendanceSearch.toLowerCase()) ||
      record.id.toLowerCase().includes(attendanceSearch.toLowerCase()) ||
      (record.deskusername && record.deskusername.toLowerCase().includes(attendanceSearch.toLowerCase()));

    // Status filter
    const matchesStatus =
      attendanceStatusFilter === "ALL" || record.status.toLowerCase() === attendanceStatusFilter.toLowerCase();

    // Date / Duration filter
    const matchesDate = isDateInRange(
      record.isoDate || record.date,
      attendanceDateFilter,
      attendanceSingleDate,
      attendanceStartDate,
      attendanceEndDate
    );

    return matchesSearch && matchesStatus && matchesDate;
  });

  // Filtered Activity Logs
  const filteredLogs = activityLogs.filter((log) => {
    const matchesType = logTypeFilter === "ALL" || log.type === logTypeFilter;
    const matchesSearch =
      log.title.toLowerCase().includes(logSearch.toLowerCase()) ||
      log.details.toLowerCase().includes(logSearch.toLowerCase()) ||
      log.type.toLowerCase().includes(logSearch.toLowerCase());
    const matchesDate = isDateInRange(
      log.isoDate || log.timestamp,
      logDateFilter,
      logSingleDate,
      logStartDate,
      logEndDate
    );
    return matchesType && matchesSearch && matchesDate;
  });

  // Filtered Break Records
  const filteredBreaks = breakHistory.filter((b) => {
    const matchesSearch =
      !breakSearch ||
      b.type.toLowerCase().includes(breakSearch.toLowerCase()) ||
      (b.reason && b.reason.toLowerCase().includes(breakSearch.toLowerCase())) ||
      b.id.toLowerCase().includes(breakSearch.toLowerCase());

    const matchesDate = isDateInRange(
      b.isoDate || b.date,
      breakDateFilter,
      breakSingleDate,
      breakStartDate,
      breakEndDate
    );

    return matchesSearch && matchesDate;
  });

  // Export Breaks to CSV
  const exportBreaksCSV = (breaksList) => {
    const headers = ["Break ID", "Type", "Reason", "Date", "Start Time", "End Time", "Duration", "Status"];
    const rows = breaksList.map((b) => [
      `"${b.id}"`,
      `"${b.type}"`,
      `"${b.reason || b.type}"`,
      `"${b.date}"`,
      `"${b.startTime}"`,
      `"${b.endTime}"`,
      `"${b.durationFormatted}"`,
      `"${b.status}"`,
    ]);
    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Petropulse_Break_History_${getIsoDateStr(Date.now())}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Attendance pagination slices
  const totalAttendancePages = Math.ceil(filteredAttendance.length / attendancePageSize) || 1;
  const paginatedAttendance = filteredAttendance.slice(
    (attendancePage - 1) * attendancePageSize,
    attendancePage * attendancePageSize
  );
  const attendanceStartIndex = filteredAttendance.length === 0 ? 0 : (attendancePage - 1) * attendancePageSize + 1;
  const attendanceEndIndex = Math.min(filteredAttendance.length, attendancePage * attendancePageSize);

  // Logs pagination slices
  const totalLogsPages = Math.ceil(filteredLogs.length / logsPageSize) || 1;
  const paginatedLogs = filteredLogs.slice(
    (logsPage - 1) * logsPageSize,
    logsPage * logsPageSize
  );
  const logsStartIndex = filteredLogs.length === 0 ? 0 : (logsPage - 1) * logsPageSize + 1;
  const logsEndIndex = Math.min(filteredLogs.length, logsPage * logsPageSize);

  // Breaks pagination slices
  const totalBreaksPages = Math.ceil(filteredBreaks.length / breaksPageSize) || 1;
  const paginatedBreaks = filteredBreaks.slice(
    (breaksPage - 1) * breaksPageSize,
    breaksPage * breaksPageSize
  );
  const breaksStartIndex = filteredBreaks.length === 0 ? 0 : (breaksPage - 1) * breaksPageSize + 1;
  const breaksEndIndex = Math.min(filteredBreaks.length, breaksPage * breaksPageSize);

  // Helper to render numbered page buttons
  const renderPaginationButtons = (curPage, totPages, onSelectPage) => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, curPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totPages, startPage + maxVisible - 1);
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    for (let number = startPage; number <= endPage; number++) {
      items.push(
        <Pagination.Item
          key={number}
          active={number === curPage}
          onClick={() => onSelectPage(number)}
        >
          {number}
        </Pagination.Item>
      );
    }
    return items;
  };

  // Render Status Badge: Active, Not Started Yet, Shift Completed
  const renderStatusBadge = () => {
    if (!isShiftActive) {
      if (isShiftEndedToday) {
        return (
          <span className="status-pill status-break" style={{ backgroundColor: "#e2e8f0", color: "#334155" }}>
            <span className="status-indicator-dot" style={{ backgroundColor: "#10b981" }} />
            Shift Completed
          </span>
        );
      }
      return (
        <span className="status-pill status-pending">
          <span className="status-indicator-dot" />
          Not Started Yet
        </span>
      );
    }
    return (
      <span className="status-pill status-active">
        <span className="status-indicator-dot" />
        Active
      </span>
    );
  };

  // Render Event Type Badge with Distinct Background & High Contrast
  const renderEventBadge = (type) => {
    const eventType = (type || "").toUpperCase().trim();
    let badgeClass = "badge-event-default";

    if (eventType === "BREAK_END") {
      badgeClass = "badge-event-break-end";
    } else if (eventType === "BREAK_START") {
      badgeClass = "badge-event-break-start";
    } else if (eventType === "LOGIN" || eventType === "SHIFT_START") {
      badgeClass = "badge-event-login";
    } else if (eventType === "LOGOUT" || eventType === "SHIFT_END") {
      badgeClass = "badge-event-logout";
    } else if (eventType === "ACTIVE") {
      badgeClass = "badge-event-active";
    } else if (eventType === "INACTIVE" || eventType === "IDLE" || eventType === "LOCKED") {
      badgeClass = "badge-event-inactive";
    }

    return (
      <span className={`tracker-event-badge ${badgeClass}`}>
        {type}
      </span>
    );
  };

  return (
    <div className="activity-tracker-container">
      <Container fluid className="px-2 px-sm-3 px-lg-4">
        {/* TOP EMPLOYEE HEADER CARD */}
        <div className="tracker-header-card">
          <Row className="align-items-center g-3 tracker-header-row">
            {/* 1. Employee Profile */}
            <Col xs={12} md={6} lg={4} className="tracker-header-profile-col">
              <div className="d-flex align-items-center gap-3 tracker-employee-profile">
                <div className="tracker-employee-avatar">
                  {employee.firstName ? employee.firstName.charAt(0).toUpperCase() : "E"}
                </div>
                <div className="tracker-employee-info">
                  <div className="tracker-emp-title">{employee.firstName}</div>
                  <div className="tracker-emp-sub">
                    <span className="fw-bold text-white">ID:</span>{" "}
                    <span className="tracker-emp-id-text" title={employee.id}>{employee.id}</span>
                    <span className="tracker-emp-dot">&bull;</span>
                    <span className="badge bg-info text-white rounded-pill ms-1 tracker-role-badge">
                      {employee.role || "Employee"}
                    </span>
                  </div>
                </div>
              </div>
            </Col>

            {/* 2. Current Working Hours Clock Box */}
            <Col xs={12} md={6} lg={4} className="text-center tracker-header-clock-col">
              <div className="tracker-clock-box" title="Current Working Hours from shift start until end shift">
                <div className="tracker-clock-icon-wrap">
                  <FontAwesomeIcon icon={faUserClock} />
                </div>
                <div className="text-start tracker-clock-content">
                  <div className="tracker-clock-label">CURRENT WORKING HOURS</div>
                  <div className="tracker-live-time font-monospace fw-bold">
                    {formatDuration(currentWorkingSeconds)}
                  </div>
                  <div className="tracker-live-sub">
                    {isShiftActive
                      ? `Shift started at ${formatTimeStr(sessionStartTime)} • ${formatDurationHuman(currentWorkingSeconds)}`
                      : isShiftEndedToday
                        ? `Shift Ended • ${todayCompletedRecord?.loginTime || formatTimeStr(sessionStartTime)} to ${todayCompletedRecord?.logoutTime || "End"} (${formatDurationHuman(currentWorkingSeconds)})`
                        : "Shift Not Started Today"}
                  </div>
                </div>
              </div>
            </Col>

            {/* 3. Status Badge and Controls */}
            <Col xs={12} md={12} lg={4} className="tracker-header-actions-col-wrap">
              <div className="tracker-header-actions-col">
                <div className="tracker-status-row">
                  {renderStatusBadge()}
                </div>

                <div className="tracker-controls-row">
                  {/* Laptop Sensor Status / Sensor Ready button and text commented out for now as requested
                  {isShiftActive || systemTrackingEnabled ? (
                    <span
                      className="badge tracker-sensor-badge active shadow-sm"
                      title="Desktop activity tracking active"
                    >
                      <FontAwesomeIcon icon={faLaptop} />
                      <span>Laptop Sensor On</span>
                    </span>
                  ) : (
                    <span
                      className="badge tracker-sensor-badge ready"
                      onClick={requestSystemIdleTracking}
                      role="button"
                      tabIndex={0}
                      title="Click to enable desktop sensor"
                    >
                      <FontAwesomeIcon icon={faLaptop} />
                      <span>Sensor Ready</span>
                    </span>
                  )}
                  */}

                  {/* Shift Control Button */}
                  {!isShiftActive ? (
                    isShiftEndedToday ? (
                      <div className="d-flex align-items-center gap-2 tracker-shift-ended-wrap">
                        <span className="badge bg-secondary py-2 px-3 fw-bold text-white d-inline-flex align-items-center gap-1 shadow-sm">
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                          <span>Shift Ended</span>
                        </span>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="d-flex align-items-center justify-content-center gap-2 fw-bold text-white border-danger btn-end-shift-header"
                          onClick={() => setShowLogoutModal(true)}
                          title="Click to end shift or update logout time for today"
                        >
                          <FontAwesomeIcon icon={faStop} />
                          <span>End Shift</span>
                        </Button>
                      </div>
                    ) : (
                      <Button
                        className="btn-start-shift d-flex align-items-center justify-content-center gap-2 fw-bold"
                        size="sm"
                        disabled={isStartingShift}
                        onClick={() => handleStartShift()}
                      >
                        {isStartingShift ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faPlay} />
                        )}
                        <span>{isStartingShift ? "Starting Shift..." : "Start Shift "}</span>
                      </Button>
                    )
                  ) : (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="d-flex align-items-center justify-content-center gap-2 fw-bold text-white border-danger btn-end-shift-header"
                      onClick={() => setShowLogoutModal(true)}
                    >
                      <FontAwesomeIcon icon={faStop} />
                      <span>End Shift</span>
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        </div>

        {/* NAVIGATION TABS */}
        <ul className="nav tracker-nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "dashboard" ? "active" : ""}`}
              onClick={() => handleTabChange("dashboard")}
            >
              <FontAwesomeIcon icon={faDesktop} />
              <span>Employee Dashboard</span>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "attendance" ? "active" : ""}`}
              onClick={() => handleTabChange("attendance")}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>Attendance History</span>
              <Badge bg="primary" pill className="ms-1">
                {attendanceHistory.length}
              </Badge>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "logs" ? "active" : ""}`}
              onClick={() => handleTabChange("logs")}
            >
              <FontAwesomeIcon icon={faHistory} />
              <span>Activity Logs</span>
              <Badge bg="secondary" pill className="ms-1">
                {activityLogs.length}
              </Badge>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "breaks" ? "active" : ""}`}
              onClick={() => handleTabChange("breaks")}
            >
              <FontAwesomeIcon icon={faCoffee} />
              <span>Break History</span>
              <Badge bg="info" pill className="ms-1">
                {breakHistory.length}
              </Badge>
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "tasks" ? "active" : ""}`}
              onClick={() => handleTabChange("tasks")}
            >
              <FontAwesomeIcon icon={faTasks} />
              <span>My Tasks</span>
              <Badge bg="success" pill className="ms-1">
                Active
              </Badge>
            </button>
          </li>
        </ul>

        {/* =========================================================================
            TAB 1: DASHBOARD OVERVIEW
           ========================================================================= */}
        {activeTab === "dashboard" && (
          <>
            {/* LIVE BREAK ACTIVE BANNER */}
            {isOnBreak && currentBreak && (() => {
              const expectedMin = currentBreak.expectedDurationMinutes || 15;
              const expectedTotalSec = expectedMin * 60;
              const elapsedSec = currentBreak.liveSeconds || 0;
              const remainingSec = Math.max(0, expectedTotalSec - elapsedSec);

              return (
                <div className="live-break-banner">
                  <Row className="align-items-center g-3">
                    <Col xs={12} lg={6}>
                      <div className="d-flex align-items-center gap-3">
                        <div className="fs-1 text-warning">
                          {currentBreak.type.includes("Lunch") ? (
                            <FontAwesomeIcon icon={faUtensils} />
                          ) : currentBreak.type.includes("Personal") ? (
                            <FontAwesomeIcon icon={faUserClock} className="text-info" />
                          ) : (
                            <FontAwesomeIcon icon={faCoffee} />
                          )}
                        </div>
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                            <Badge bg="warning" text="dark" className="text-uppercase fw-bold">
                              Current Status: On Break
                            </Badge>
                            <Badge bg="info" className="text-white">
                              Work Counter Paused
                            </Badge>
                          </div>
                          <h4 className="text-white mb-0">{currentBreak.type} in Progress</h4>
                          {currentBreak.reason && (
                            <div className="text-white opacity-90 small mt-1">
                              <span className="fw-bold text-warning">Reason:</span>{" "}
                              <span>{currentBreak.reason}</span>
                            </div>
                          )}
                          <small className="text-light opacity-75">
                            Started at: {currentBreak.startTimeStr || formatTimeStr(currentBreak.startTime)} &bull; Duration: {expectedMin} mins &bull; Auto-finishes at 00:00:00
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12} lg={6} className="text-center text-lg-end">
                      <div className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-end gap-3 live-break-banner-actions">
                        <div className="text-center">
                          <div className="small text-warning text-uppercase fw-bold">Break Countdown</div>
                          <div className="live-break-timer text-warning">
                            {formatDuration(remainingSec)}
                          </div>
                          <small className="text-light opacity-75" style={{ fontSize: "0.72rem" }}>
                            Auto-resumes countdown when finished
                          </small>
                        </div>
                        <div className="text-center d-none d-sm-block">
                          <div className="small text-light text-uppercase opacity-75">Elapsed</div>
                          <div className="live-break-timer opacity-50" style={{ fontSize: "1.7rem" }}>
                            {formatDuration(elapsedSec)}
                          </div>
                        </div>
                        <Button
                          variant="danger"
                          size="lg"
                          className="d-flex align-items-center justify-content-center gap-2 fw-bold shadow-lg btn-end-break"
                          onClick={endBreak}
                          title="Resume active work before scheduled duration completes"
                        >
                          <FontAwesomeIcon icon={faStop} />
                          <span>End Break</span>
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </div>
              );
            })()}

            {/* SHIFT STATUS DASHBOARD BANNER */}
            {!isShiftActive && !isShiftEndedToday && (
              <div className="alert alert-info border border-info border-opacity-25 shadow-sm p-3 mb-4 rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
                <div className="d-flex align-items-center gap-3">
                  <div className="fs-2 text-primary">
                    <FontAwesomeIcon icon={faUserClock} />
                  </div>
                  <div>
                    <h5 className="mb-1 text-dark fw-bold">Shift Not Started Yet</h5>
                    <div className="text-muted small">
                      Your shift tracking for today has not started yet. Click <strong>Start Shift Now</strong> to begin recording your work hours and activity.
                    </div>
                  </div>
                </div>
                <Button
                  variant="success"
                  className="fw-bold d-flex align-items-center gap-2 shadow-sm"
                  disabled={isStartingShift}
                  onClick={() => handleStartShift()}
                >
                  {isStartingShift ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FontAwesomeIcon icon={faPlay} />
                  )}
                  <span>{isStartingShift ? "Starting Shift..." : "Start Shift Now"}</span>
                </Button>
              </div>
            )}

            {!isShiftActive && isShiftEndedToday && (
              <div className="alert alert-secondary border border-secondary border-opacity-25 shadow-sm p-3 mb-4 rounded-3 d-flex flex-wrap align-items-center justify-content-between gap-3 bg-light">
                <div className="d-flex align-items-center gap-3">
                  <div className="fs-2 text-success">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </div>
                  <div>
                    <h5 className="mb-1 text-dark fw-bold">Shift Ended for Today</h5>
                    <div className="text-muted small">
                      You completed your shift today{todayCompletedRecord?.logoutTime ? ` at ${todayCompletedRecord.logoutTime}` : ""}. You can click <strong>End Shift</strong> below or in the header whenever you want to re-record or update your shift end time.
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="fw-bold d-flex align-items-center gap-2 px-3 py-2 shadow-sm"
                  onClick={() => setShowLogoutModal(true)}
                  title="Click to update or record shift end time"
                >
                  <FontAwesomeIcon icon={faStop} />
                  <span>End Shift</span>
                </Button>
              </div>
            )}

            {/* 4 CORE METRIC CARDS */}
            <Row className="g-3 mb-4">
              {/* 1. Total Active Time */}
              <Col xs={12} sm={6} xl={3}>
                <Card
                  className="tracker-stat-card card-active card-interactive h-100"
                  onClick={() => {
                    setLogTypeFilter("ACTIVE");
                    handleTabChange("logs");
                  }}
                  role="button"
                  title="Click to view active work logs"
                >
                  <Card.Body>
                    <div className="tracker-stat-header">
                      <span className="tracker-stat-label">Active Time</span>
                      <div className="tracker-stat-icon-wrap stat-icon-active">
                        <FontAwesomeIcon icon={faUserClock} />
                      </div>
                    </div>
                    <div className="tracker-stat-value-wrap">
                      <div className="tracker-stat-value text-active">{formatDuration(activeSeconds)}</div>
                    </div>
                    <div className="tracker-stat-footer justify-content-between">
                      <span className="tracker-stat-subtext">{formatDurationHuman(activeSeconds)}</span>
                      {status === "ON_BREAK" ? (
                        <span className="badge bg-warning text-dark fw-bold">
                          Paused for Break
                        </span>
                      ) : (
                        <span className="badge bg-success text-white fw-bold">
                          {activePercent}% of shift
                        </span>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* 2. Total Inactive / Idle Time */}
              <Col xs={12} sm={6} xl={3}>
                <Card
                  className="tracker-stat-card card-inactive card-interactive h-100"
                  onClick={() => {
                    setLogTypeFilter("INACTIVE");
                    handleTabChange("logs");
                  }}
                  role="button"
                  title="Click to view inactive & idle logs"
                >
                  <Card.Body>
                    <div className="tracker-stat-header">
                      <span className="tracker-stat-label">Inactive / Idle Time</span>
                      <div className="tracker-stat-icon-wrap stat-icon-inactive">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                      </div>
                    </div>
                    <div className="tracker-stat-value-wrap">
                      <div className="tracker-stat-value text-inactive">{formatDuration(inactiveSeconds)}</div>
                    </div>
                    <div className="tracker-stat-footer justify-content-between">
                      <span className="tracker-stat-subtext">{formatDurationHuman(inactiveSeconds)}</span>
                      <span className="badge bg-warning text-dark fw-bold">
                        {inactivePercent}% of shift
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* 3. Total Break Time */}
              <Col xs={12} sm={6} xl={3}>
                <Card
                  className="tracker-stat-card card-break card-interactive h-100"
                  onClick={() => handleTabChange("breaks")}
                  role="button"
                  title="Click to view detailed break history"
                >
                  <Card.Body>
                    <div className="tracker-stat-header">
                      <span className="tracker-stat-label">Total Break Time</span>
                      <div className="tracker-stat-icon-wrap stat-icon-break">
                        <FontAwesomeIcon icon={faCoffee} />
                      </div>
                    </div>
                    <div className="tracker-stat-value-wrap">
                      <div className="tracker-stat-value text-break">
                        {formatBreakTimeMinutes(breakSeconds)}
                      </div>
                    </div>
                    <div className="tracker-stat-footer justify-content-between">
                      <span className="tracker-stat-subtext text-muted fw-semibold">{formatBreakTimeMinutes(breakSeconds)}</span>
                      <span className="badge bg-primary text-white fw-bold">
                        {breakHistory.length} {breakHistory.length === 1 ? "break taken" : "breaks taken"}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* 4. Total Session Duration */}
              <Col xs={12} sm={6} xl={3}>
                <Card
                  className="tracker-stat-card card-session card-interactive h-100"
                  onClick={() => handleTabChange("attendance")}
                  role="button"
                  title="Click to view daily attendance records"
                >
                  <Card.Body>
                    <div className="tracker-stat-header">
                      <span className="tracker-stat-label">Total Shift Duration</span>
                      <div className="tracker-stat-icon-wrap stat-icon-session">
                        <FontAwesomeIcon icon={faStopwatch} />
                      </div>
                    </div>
                    <div className="tracker-stat-value-wrap">
                      <div className="tracker-stat-value text-session">{formatDuration(currentWorkingSeconds)}</div>
                    </div>
                    <div className="tracker-stat-footer justify-content-between">
                      <span className="tracker-stat-subtext">
                        {isShiftActive
                          ? `Logged in: ${formatTimeStr(sessionStartTime)}`
                          : isShiftEndedToday
                            ? `Shift Ended • ${todayCompletedRecord ? `${todayCompletedRecord.loginTime} - ${todayCompletedRecord.logoutTime}` : "Saved"}`
                            : "Logged in: Not Started"}
                      </span>
                      <span
                        className={`badge ${isShiftActive
                          ? "bg-primary text-white"
                          : isShiftEndedToday
                            ? "bg-secondary text-white"
                            : "bg-warning text-dark"
                          } fw-bold`}
                      >
                        {isShiftActive
                          ? "Shift Active"
                          : isShiftEndedToday
                            ? "Shift Completed"
                            : "Shift Pending"}
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* BREAK MANAGEMENT & SHIFT PROGRESS SECTION */}
            <Row className="g-3 mb-4">
              {/* Left Column: Quick Break Starter */}
              <Col xs={12} lg={6}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <h5 className="mb-0 text-dark fw-bold d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCoffee} className="text-primary" />
                        Break Management
                      </h5>
                      {status === "ON_BREAK" ? (
                        <Badge bg="danger">Break Active</Badge>
                      ) : (
                        <Badge bg="success">Working</Badge>
                      )}
                    </div>
                  </Card.Header>
                  <Card.Body className="p-3 p-md-4">
                    <p className="text-muted small mb-3">
                      Select a break type below to pause activity tracking. During breaks, your main status will remain
                      <strong> On Break</strong>.
                    </p>

                    {isOnBreak && (
                      <div className="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center gap-2 small border-0 shadow-sm" style={{ backgroundColor: "#fef3c7", color: "#92400e" }}>
                        <FontAwesomeIcon icon={faLock} className="text-warning fs-5 flex-shrink-0" />
                        <div>
                          <strong>{currentBreak ? currentBreak.type : "Break"} in progress:</strong> The other 2 breaks are locked until you click <strong>End Break</strong>.
                        </div>
                      </div>
                    )}

                    <Row className="g-2 mb-3">
                      {/* Tea Break Card */}
                      <Col xs={4}>
                        <div
                          className={`break-select-card ${isTeaActive
                            ? "selected active-running-break"
                            : selectedBreakType === "Tea Break" && !isOnBreak
                              ? "selected"
                              : ""
                            } ${isTeaDisabled ? "break-card-disabled" : ""}`}
                          onClick={() => !isTeaDisabled && handleSelectBreakType("Tea Break")}
                          role="button"
                          aria-disabled={isTeaDisabled}
                          title={isTeaDisabled && isOnBreak ? "Disabled until active break is ended" : "Tea Break (~15 min)"}
                        >
                          <div className="break-card-icon text-warning">
                            <FontAwesomeIcon icon={faCoffee} />
                          </div>
                          <div className="fw-bold small text-dark">Tea Break</div>
                          {isTeaActive ? (
                            <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.68rem" }}>
                              Active Now
                            </Badge>
                          ) : isOnBreak ? (
                            <Badge bg="secondary" className="mt-1" style={{ fontSize: "0.65rem", opacity: 0.85 }}>
                              <FontAwesomeIcon icon={faLock} className="me-1" />
                              Disabled
                            </Badge>
                          ) : (
                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              ~15 min
                            </div>
                          )}
                        </div>
                      </Col>

                      {/* Lunch Break Card */}
                      <Col xs={4}>
                        <div
                          className={`break-select-card ${isLunchActive
                            ? "selected active-running-break"
                            : selectedBreakType === "Lunch Break" && !isOnBreak
                              ? "selected"
                              : ""
                            } ${isLunchDisabled ? "break-card-disabled" : ""}`}
                          onClick={() => !isLunchDisabled && handleSelectBreakType("Lunch Break")}
                          role="button"
                          aria-disabled={isLunchDisabled}
                          title={isLunchDisabled && isOnBreak ? "Disabled until active break is ended" : "Lunch Break (~30 min)"}
                        >
                          <div className="break-card-icon text-primary">
                            <FontAwesomeIcon icon={faUtensils} />
                          </div>
                          <div className="fw-bold small text-dark">Lunch Break</div>
                          {isLunchActive ? (
                            <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.68rem" }}>
                              Active Now
                            </Badge>
                          ) : isOnBreak ? (
                            <Badge bg="secondary" className="mt-1" style={{ fontSize: "0.65rem", opacity: 0.85 }}>
                              <FontAwesomeIcon icon={faLock} className="me-1" />
                              Disabled
                            </Badge>
                          ) : (
                            <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                              ~30 min
                            </div>
                          )}
                        </div>
                      </Col>

                      {/* Personal Break Card */}
                      <Col xs={4}>
                        <div
                          className={`break-select-card ${isPersonalActive
                            ? "selected active-running-break"
                            : selectedBreakType === "Personal Break" && !isOnBreak
                              ? "selected"
                              : ""
                            } ${isPersonalDisabled ? "break-card-disabled" : ""}`}
                          onClick={() => !isPersonalDisabled && handleSelectBreakType("Personal Break")}
                          role="button"
                          aria-disabled={isPersonalDisabled}
                          title={isPersonalDisabled && isOnBreak ? "Disabled until active break is ended" : "Personal Break"}
                        >
                          <div className="break-card-icon text-info">
                            <FontAwesomeIcon icon={faUserClock} />
                          </div>
                          <div className="fw-bold small text-dark">Personal</div>
                          {isPersonalActive ? (
                            <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.68rem" }}>
                              Active Now
                            </Badge>
                          ) : isOnBreak ? (
                            <Badge bg="secondary" className="mt-1" style={{ fontSize: "0.65rem", opacity: 0.85 }}>
                              <FontAwesomeIcon icon={faLock} className="me-1" />
                              Disabled
                            </Badge>
                          ) : (
                            <div className="text-info fw-bold" style={{ fontSize: "0.75rem" }}>
                              User's Choice
                            </div>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {isOnBreak ? (
                      <Button
                        variant="danger"
                        className="w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2"
                        onClick={endBreak}
                      >
                        <FontAwesomeIcon icon={faStop} />
                        End {currentBreak ? currentBreak.type : "Break"} Now
                      </Button>
                    ) : (
                      <Button
                        variant={selectedBreakType === "Personal Break" ? "info" : "primary"}
                        disabled={!isShiftActive || isStartingBreak}
                        className={`w-100 py-2 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm ${selectedBreakType === "Personal Break" ? "text-white" : ""
                          }`}
                        onClick={handleDashboardBreakClick}
                      >
                        {isStartingBreak ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FontAwesomeIcon icon={faPlay} />
                        )}
                        {!isShiftActive
                          ? isShiftEndedToday
                            ? "Shift Ended for Today"
                            : "Start Shift First"
                          : isStartingBreak
                            ? "Updating Break on Server..."
                            : `Start ${selectedBreakType}${selectedBreakType === "Personal Break" ? " (Enter Reason)" : ""}`}
                      </Button>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {/* Right Column: Shift Breakdown & Progress */}
              <Col xs={12} lg={6}>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom py-3">
                    <h5 className="mb-0 text-dark fw-bold d-flex align-items-center gap-2">
                      <FontAwesomeIcon icon={faStopwatch} className="text-info" />
                      Session Distribution
                    </h5>
                  </Card.Header>
                  <Card.Body className="p-3 p-md-4">
                    <div className="mb-3">
                      <div className="d-flex justify-content-between small text-muted mb-1">
                        <span>Total Tracked Duration</span>
                        <span className="fw-bold text-dark">{formatDurationHuman(sessionSeconds)}</span>
                      </div>
                      <div className="session-split-bar">
                        <div className="split-active" style={{ width: `${activePercent}%` }} title={`Active: ${activePercent}%`} />
                        <div className="split-break" style={{ width: `${breakPercent}%` }} title={`Break: ${breakPercent}%`} />
                        <div className="split-inactive" style={{ width: `${inactivePercent}%` }} title={`Idle: ${inactivePercent}%`} />
                      </div>
                    </div>

                    <div className="row g-2 text-center pt-2">
                      <div className="col-4">
                        <div className="p-2 border rounded bg-light session-distribution-col">
                          <div className="small text-muted d-flex align-items-center justify-content-center gap-1">
                            <span className="badge bg-success rounded-circle p-1" />
                            Active
                          </div>
                          <div className="fw-bold text-success fs-6">{activePercent}%</div>
                          <small className="text-muted">{formatDuration(activeSeconds)}</small>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="p-2 border rounded bg-light session-distribution-col">
                          <div className="small text-muted d-flex align-items-center justify-content-center gap-1">
                            <span className="badge bg-primary rounded-circle p-1" />
                            Break
                          </div>
                          <div className="fw-bold text-primary fs-6">{breakPercent}%</div>
                          <small className="text-muted">{formatDuration(breakSeconds)}</small>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="p-2 border rounded bg-light session-distribution-col">
                          <div className="small text-muted d-flex align-items-center justify-content-center gap-1">
                            <span className="badge bg-warning rounded-circle p-1" />
                            Inactive
                          </div>
                          <div className="fw-bold text-warning fs-6">{inactivePercent}%</div>
                          <small className="text-muted">{formatDuration(inactiveSeconds)}</small>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 p-2 rounded bg-soft small text-muted d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <span>
                        Shift status:{" "}
                        <strong>
                          {isShiftActive
                            ? `Started at ${formatTimeStr(sessionStartTime)}`
                            : isShiftEndedToday
                              ? `Shift Ended for Today (${todayCompletedRecord?.loginTime || "Recorded"} - ${todayCompletedRecord?.logoutTime || "Ended"})`
                              : "Not Started (Ready to Start Shift)"}
                        </strong>
                      </span>
                      <span className="text-primary fw-bold cursor-pointer" onClick={() => handleTabChange("logs")}>
                        View Full Logs &rarr;
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* MY ACTIVE TASKS & PROJECT WORKLOAD BANNER */}
            <Card className="shadow-sm border-0 mb-4 task-dashboard-quick-card">
              <Card.Body className="p-3">
                <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="task-quick-icon bg-primary text-white p-3 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: "46px", height: "46px" }}>
                      <FontAwesomeIcon icon={faTasks} className="fs-5" />
                    </div>
                    <div>
                      <h6 className="mb-1 fw-bold text-dark d-flex align-items-center gap-2 flex-wrap">
                        <span>My Assigned Tasks &amp; Project Work</span>
                        <Badge bg="primary" pill>My Tasks</Badge>
                      </h6>
                      <div className="text-muted fs-7">
                        View tasks grouped by status, create tasks under project, and log work notes
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2 w-100-mobile">
                    <Button
                      variant="primary"
                      size="sm"
                      className="fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm w-100-mobile"
                      onClick={() => handleTabChange("tasks")}
                    >
                      <FontAwesomeIcon icon={faTasks} />
                      <span>Open My Tasks &amp; Kanban Board &rarr;</span>
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* QUICK PREVIEWS ROW */}
            <Row className="g-3">
              {/* Recent Activity Logs Preview */}
              <Col xs={12} lg={7}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                      <FontAwesomeIcon icon={faHistory} className="text-secondary" />
                      Recent Activity Events
                    </h6>
                    <Button variant="link" size="sm" className="p-0 text-primary" onClick={() => handleTabChange("logs")}>
                      View All Logs
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="tracker-table mb-0">
                        <thead>
                          <tr>
                            <th>Time</th>
                            <th>Event</th>
                            <th>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activityLogs.slice(0, 5).map((log) => (
                            <tr key={log.id}>
                              <td className="text-muted small text-nowrap">{log.timeStr}</td>
                              <td>
                                {renderEventBadge(log.type)}
                              </td>
                              <td className="small text-dark">{log.title}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Today's Breaks Preview */}
              <Col xs={12} lg={5}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                      <FontAwesomeIcon icon={faCoffee} className="text-warning" />
                      Today&apos;s Breaks
                    </h6>
                    <Button variant="link" size="sm" className="p-0 text-primary" onClick={() => handleTabChange("breaks")}>
                      View History
                    </Button>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <Table hover className="tracker-table mb-0">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Time</th>
                            <th>Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {breakHistory.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center text-muted py-3 small">
                                No breaks recorded yet today.
                              </td>
                            </tr>
                          ) : (
                            breakHistory.slice(0, 4).map((b, idx) => (
                              <tr key={b?.id ? `${b.id}-${idx}` : `brk-${idx}`}>
                                <td className="fw-bold small text-dark">{b.type}</td>
                                <td className="small text-muted">{b.startTime}</td>
                                <td className="small fw-bold text-primary">{b.durationFormatted}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {/* =========================================================================
            TAB 2: ATTENDANCE HISTORY
           ========================================================================= */}
        {activeTab === "attendance" && (
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-3">
              <Row className="align-items-center g-3">
                <Col xs={12} md={4}>
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-primary" />
                    Attendance Records
                  </h5>
                  <small className="text-muted">Review login, active, break, and total shift hours</small>
                </Col>

                {/* Filters */}
                <Col xs={12} md={8}>
                  <div className="d-flex flex-wrap align-items-center justify-content-md-end gap-2 tracker-table-toolbar">
                    {/* Search */}
                    <InputGroup size="sm" className="tracker-filter-search" style={{ maxWidth: "200px" }}>
                      <InputGroup.Text className="bg-light border-end-0">
                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search records..."
                        className="border-start-0"
                        value={attendanceSearch}
                        onChange={(e) => setAttendanceSearch(e.target.value)}
                      />
                    </InputGroup>

                    {/* Date / Duration filter */}
                    <Form.Select
                      size="sm"
                      className="tracker-filter-select"
                      style={{ maxWidth: "165px" }}
                      value={attendanceDateFilter}
                      onChange={(e) => setAttendanceDateFilter(e.target.value)}
                    >
                      <option value="ALL">All Dates</option>
                      <option value="TODAY">Today</option>
                      <option value="YESTERDAY">Yesterday</option>
                      <option value="LAST_7_DAYS">Last 7 Days (Duration)</option>
                      <option value="LAST_30_DAYS">Last 30 Days (Duration)</option>
                      <option value="THIS_MONTH">This Month (Duration)</option>
                      <option value="SINGLE_DATE">Specific Date...</option>
                      <option value="CUSTOM_RANGE">Custom Duration (From - To)...</option>
                    </Form.Select>

                    {attendanceDateFilter === "SINGLE_DATE" && (
                      <Form.Control
                        type="date"
                        size="sm"
                        className="tracker-filter-select"
                        style={{ maxWidth: "140px" }}
                        value={attendanceSingleDate}
                        onChange={(e) => setAttendanceSingleDate(e.target.value)}
                      />
                    )}

                    {attendanceDateFilter === "CUSTOM_RANGE" && (
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          type="date"
                          size="sm"
                          style={{ maxWidth: "135px" }}
                          value={attendanceStartDate}
                          onChange={(e) => setAttendanceStartDate(e.target.value)}
                          title="From Date"
                        />
                        <span className="small text-muted">to</span>
                        <Form.Control
                          type="date"
                          size="sm"
                          style={{ maxWidth: "135px" }}
                          value={attendanceEndDate}
                          onChange={(e) => setAttendanceEndDate(e.target.value)}
                          title="To Date"
                        />
                      </div>
                    )}

                    {/* Status filter */}
                    <Form.Select
                      size="sm"
                      className="tracker-filter-select"
                      style={{ maxWidth: "140px" }}
                      value={attendanceStatusFilter}
                      onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                    >
                      <option value="ALL">All Status</option>
                      <option value="Completed">Completed</option>
                      <option value="Half-Day">Half-Day</option>
                    </Form.Select>

                    {/* Sync / Refresh from Server */}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-flex align-items-center justify-content-center gap-1 fw-bold tracker-filter-btn"
                      disabled={isLoadingAttendance}
                      onClick={() => fetchAttendanceHistory()}
                      title="Sync attendance records from server"
                    >
                      <FontAwesomeIcon icon={faHistory} spin={isLoadingAttendance} />
                      <span>{isLoadingAttendance ? "Syncing..." : "Sync"}</span>
                    </Button>

                    {/* Export CSV button */}
                    <Button
                      variant="success"
                      size="sm"
                      className="d-flex align-items-center justify-content-center gap-2 fw-bold tracker-filter-btn"
                      onClick={() => exportAttendanceCSV(filteredAttendance)}
                    >
                      <FontAwesomeIcon icon={faFileCsv} />
                      <span>Export CSV</span>
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="p-0">
              {isShiftEndedToday && (
                <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 text-muted small">
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-6" />
                    <span>
                      Today's shift was completed and recorded in the history below
                      {todayCompletedRecord ? ` (${todayCompletedRecord.loginTime} - ${todayCompletedRecord.logoutTime})` : ""}.
                    </span>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="py-1 px-3 fw-bold d-flex align-items-center gap-2 shadow-sm"
                    onClick={() => setShowLogoutModal(true)}
                    title="Click to end shift or update logout time for today"
                  >
                    <FontAwesomeIcon icon={faStop} />
                    <span>End Shift</span>
                  </Button>
                </div>
              )}

              {!isShiftActive && !isShiftEndedToday && (
                <div className="p-3 bg-light border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2 text-muted small">
                  <div className="d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faClock} className="text-warning fs-6" />
                    <span>Today's shift has not been started yet.</span>
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="py-1 px-3 fw-bold"
                    disabled={isStartingShift}
                    onClick={() => handleStartShift()}
                  >
                    {isStartingShift ? "Starting Shift..." : "Start Shift"}
                  </Button>
                </div>
              )}

              <div className="tracker-table-vertical-wrap">
                <div className="mobile-table-scroll-hint">
                  <span>Swipe horizontally to view all columns &rarr;</span>
                </div>
                <Table hover className="tracker-table mb-0">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Login Time</th>
                      <th>Logout Time</th>
                      {/* <th>Screen Lock Time</th> */}
                      <th>Idle Time</th>
                      <th>Break Time</th>
                      <th>Active Time</th>
                      <th>Total Shift</th>
                      <th>Status</th>
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Current In-Progress Shift Row (shown only if server list does not already contain today's live record) */}
                    {isShiftActive &&
                      !paginatedAttendance.some((r) => (r.date === todayDateStr || r.isoDate === getIsoDateStr(Date.now())) && (r.status === "In Progress" || r.logoutTime === "--" || r.logoutTime === "0")) &&
                      isDateInRange(Date.now(), attendanceDateFilter, attendanceSingleDate, attendanceStartDate, attendanceEndDate) && (
                        <tr className="bg-light-blue" style={{ backgroundColor: "#f0f9ff" }}>
                          <td className="fw-bold text-primary">
                            {formatDateStr(sessionStartTime)} <Badge bg="primary" className="ms-1">Today</Badge>
                          </td>
                          <td>
                            <span className="fw-bold text-dark">{formatTimeStr(sessionStartTime)}</span>
                          </td>
                          <td className="text-muted fst-italic">-- (Active)</td>
                          {/* <td className="text-warning fw-bold font-monospace">{formatDuration(screenLockSeconds || 0)}</td> */}
                          <td className="text-secondary fw-bold font-monospace">{formatDuration(inactiveSeconds)}</td>
                          <td className="text-info fw-bold font-monospace">{formatBreakTimeMinutes(breakSeconds)}</td>
                          <td className="text-success fw-bold font-monospace">
                            <div>
                              <span>{formatDuration(activeSeconds)}</span>
                              <div className="text-muted fw-normal" style={{ fontSize: "0.70rem" }}>
                                {formatDurationHuman(activeSeconds)}
                              </div>
                            </div>
                          </td>
                          <td className="text-dark fw-bold font-monospace">{formatDuration(currentWorkingSeconds)}</td>
                          <td>
                            <Badge bg="success">Active</Badge>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="py-0 px-2"
                              onClick={() => {
                                setSelectedAttendanceRecord({
                                  id: "TODAY-SHIFT",
                                  date: formatDateStr(sessionStartTime),
                                  deskusername: sessionStorage.getItem("userName") || employee?.userName || employee?.name || employee?.firstName || "Employee",
                                  loginTime: formatTimeStr(sessionStartTime),
                                  logoutTime: "Still Working",
                                  screenLockSeconds: screenLockSeconds || 0,
                                  desktotalScreenlocktime: String(screenLockSeconds || "0"),
                                  idleSeconds: inactiveSeconds,
                                  desktotalidletime: String(inactiveSeconds || "0"),
                                  inactiveSeconds,
                                  breakSeconds,
                                  breaktime: String(breakSeconds || "0"),
                                  screenActiveTime: activeSeconds,
                                  screenactivetime: String(activeSeconds),
                                  activeSeconds,
                                  totalSeconds: currentWorkingSeconds || sessionSeconds,
                                  status: "In Progress",
                                });
                                setShowDetailModal(true);
                              }}
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </Button>
                          </td>
                        </tr>
                      )}

                    {/* Loading State */}
                    {isLoadingAttendance && paginatedAttendance.length === 0 && (
                      <tr>
                        <td colSpan="9" className="text-center py-4">
                          <Spinner animation="border" size="sm" variant="primary" className="me-2" />
                          <span className="text-muted small">Loading attendance records from server...</span>
                        </td>
                      </tr>
                    )}

                    {/* Empty State */}
                    {!isLoadingAttendance && paginatedAttendance.length === 0 && (!isShiftActive || !isDateInRange(Date.now(), attendanceDateFilter, attendanceSingleDate, attendanceStartDate, attendanceEndDate)) && (
                      <tr>
                        <td colSpan="9" className="text-center py-5 text-muted">
                          <div className="py-3">
                            <FontAwesomeIcon icon={faExclamationTriangle} className="text-secondary opacity-50 mb-2 fs-3" />
                            <div className="fw-bold text-dark fs-6">No data available</div>
                            <small className="text-muted">No attendance records returned from server.</small>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Historical Records */}
                    {paginatedAttendance.map((record) => (
                      <tr key={record.id}>
                        <td className="fw-bold text-dark">{record.date}</td>
                        <td className="small text-muted">{record.loginTime}</td>
                        <td className="small text-muted">{record.logoutTime}</td>
                        {/* <td className="text-warning font-monospace">
                          {formatDuration(
                            record.desktotalScreenlocktime !== undefined && record.desktotalScreenlocktime !== null && record.desktotalScreenlocktime !== ""
                              ? record.desktotalScreenlocktime
                              : (record.screenLockSeconds || 0)
                          )}
                        </td> */}
                        <td className="text-secondary font-monospace">
                          {formatDuration(
                            record.desktotalidletime !== undefined && record.desktotalidletime !== null && record.desktotalidletime !== ""
                              ? record.desktotalidletime
                              : (record.idleSeconds || record.inactiveSeconds || 0)
                          )}
                        </td>
                        <td className="text-indigo font-monospace">
                          {formatBreakTimeMinutes(
                            record.breaktime !== undefined && record.breaktime !== null && record.breaktime !== ""
                              ? record.breaktime
                              : (record.breakSeconds || 0)
                          )}
                        </td>
                        <td className="text-success font-monospace">
                          <div>
                            <span className="fw-bold">
                              {formatDuration(
                                parseScreenActiveTimeToSeconds(
                                  record.screenactivetime !== undefined && record.screenactivetime !== null && record.screenactivetime !== ""
                                    ? record.screenactivetime
                                    : (record.screenActiveTime || record.activeSeconds || 0)
                                )
                              )}
                            </span>
                            <div className="text-muted" style={{ fontSize: "0.70rem" }}>
                              {formatDurationHuman(
                                parseScreenActiveTimeToSeconds(
                                  record.screenactivetime !== undefined && record.screenactivetime !== null && record.screenactivetime !== ""
                                    ? record.screenactivetime
                                    : (record.screenActiveTime || record.activeSeconds || 0)
                                )
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="fw-bold text-dark font-monospace">{formatDuration(record.totalSeconds)}</td>
                        <td>
                          <Badge
                            bg={
                              record.status === "Completed" || record.status === "Shift Completed"
                                ? "success"
                                : record.status === "In Progress" || record.status === "Active"
                                  ? "primary"
                                  : record.status === "Half-Day"
                                    ? "warning"
                                    : "secondary"
                            }
                          >
                            {record.status === "Completed" || record.status === "Shift Completed"
                              ? "Shift Completed"
                              : record.status === "In Progress" || record.status === "Active"
                                ? "Active"
                                : record.status}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="py-0 px-2"
                            onClick={() => {
                              setSelectedAttendanceRecord(record);
                              setShowDetailModal(true);
                            }}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>

            <Card.Footer className="tracker-pagination-footer">
              <Row className="align-items-center g-2 tracker-pagination-row">
                <Col xs={12} lg={6} className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-2 tracker-pagination-info">
                  <span className="small text-muted fw-bold">Show</span>
                  <Form.Select
                    size="sm"
                    style={{ width: "75px" }}
                    value={attendancePageSize}
                    onChange={(e) => {
                      setAttendancePageSize(Number(e.target.value));
                      setAttendancePage(1);
                    }}
                    className="fw-semibold"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  <span className="small text-muted fw-bold">Entries per page</span>
                  <span className="small text-muted ms-md-3">
                    Showing <strong>{attendanceStartIndex}</strong> to <strong>{attendanceEndIndex}</strong> of{" "}
                    <strong>{filteredAttendance.length}</strong> entries
                  </span>
                </Col>
                <Col xs={12} lg={6} className="d-flex justify-content-center justify-content-lg-end align-items-center mt-2 mt-lg-0">
                  <Pagination size="sm" className="mb-0 flex-wrap justify-content-center">
                    <Pagination.First
                      disabled={attendancePage === 1}
                      onClick={() => setAttendancePage(1)}
                    />
                    <Pagination.Prev
                      disabled={attendancePage === 1}
                      onClick={() => setAttendancePage((p) => Math.max(1, p - 1))}
                    />
                    {renderPaginationButtons(attendancePage, totalAttendancePages, setAttendancePage)}
                    <Pagination.Next
                      disabled={attendancePage === totalAttendancePages || totalAttendancePages === 0}
                      onClick={() => setAttendancePage((p) => Math.min(totalAttendancePages, p + 1))}
                    />
                    <Pagination.Last
                      disabled={attendancePage === totalAttendancePages || totalAttendancePages === 0}
                      onClick={() => setAttendancePage(totalAttendancePages)}
                    />
                  </Pagination>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        )}

        {/* =========================================================================
            TAB 3: ACTIVITY LOGS AUDIT TRAIL
           ========================================================================= */}
        {activeTab === "logs" && (
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-3">
              <Row className="align-items-center g-3">
                <Col xs={12} md={4}>
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faHistory} className="text-secondary" />
                    Activity Audit Logs
                  </h5>
                  <small className="text-muted">Real-time log of state transitions and input events</small>
                </Col>

                {/* Filters */}
                <Col xs={12} md={8}>
                  <div className="d-flex flex-wrap align-items-center justify-content-md-end gap-2 tracker-table-toolbar">
                    {/* Search */}
                    <InputGroup size="sm" className="tracker-filter-search" style={{ maxWidth: "200px" }}>
                      <InputGroup.Text className="bg-light border-end-0">
                        <FontAwesomeIcon icon={faSearch} className="text-muted" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search logs..."
                        className="border-start-0"
                        value={logSearch}
                        onChange={(e) => setLogSearch(e.target.value)}
                      />
                    </InputGroup>

                    {/* Event Type Filter */}
                    <Form.Select
                      size="sm"
                      className="tracker-filter-select"
                      style={{ maxWidth: "150px" }}
                      value={logTypeFilter}
                      onChange={(e) => setLogTypeFilter(e.target.value)}
                    >
                      <option value="ALL">All Event Types</option>
                      <option value="ACTIVE">Screen Active</option>
                      <option value="INACTIVE">Screen Inactive</option>
                      <option value="BREAK_START">Break Start</option>
                      <option value="BREAK_END">Break End</option>
                      <option value="LOGIN">Login</option>
                      <option value="LOGOUT">Logout</option>
                    </Form.Select>

                    {/* Date / Duration filter */}
                    <Form.Select
                      size="sm"
                      className="tracker-filter-select"
                      style={{ maxWidth: "165px" }}
                      value={logDateFilter}
                      onChange={(e) => setLogDateFilter(e.target.value)}
                    >
                      <option value="ALL">All Dates</option>
                      <option value="TODAY">Today</option>
                      <option value="YESTERDAY">Yesterday</option>
                      <option value="LAST_7_DAYS">Last 7 Days (Duration)</option>
                      <option value="LAST_30_DAYS">Last 30 Days (Duration)</option>
                      <option value="THIS_MONTH">This Month (Duration)</option>
                      <option value="SINGLE_DATE">Specific Date...</option>
                      <option value="CUSTOM_RANGE">Custom Duration (From - To)...</option>
                    </Form.Select>

                    {logDateFilter === "SINGLE_DATE" && (
                      <Form.Control
                        type="date"
                        size="sm"
                        className="tracker-filter-select"
                        style={{ maxWidth: "140px" }}
                        value={logSingleDate}
                        onChange={(e) => setLogSingleDate(e.target.value)}
                      />
                    )}

                    {logDateFilter === "CUSTOM_RANGE" && (
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          type="date"
                          size="sm"
                          style={{ maxWidth: "135px" }}
                          value={logStartDate}
                          onChange={(e) => setLogStartDate(e.target.value)}
                          title="From Date"
                        />
                        <span className="small text-muted">to</span>
                        <Form.Control
                          type="date"
                          size="sm"
                          style={{ maxWidth: "135px" }}
                          value={logEndDate}
                          onChange={(e) => setLogEndDate(e.target.value)}
                          title="To Date"
                        />
                      </div>
                    )}

                    {/* Export Logs */}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="d-flex align-items-center justify-content-center gap-1 tracker-filter-btn"
                      onClick={exportActivityLogsCSV}
                    >
                      <FontAwesomeIcon icon={faFileCsv} />
                      <span>Export Logs</span>
                    </Button>

                    {/* Clear Logs */}
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="d-flex align-items-center justify-content-center gap-1 text-danger tracker-filter-btn"
                      onClick={clearActivityLogs}
                      title="Clear activity audit logs"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Header>

            <Card.Body className="p-0">
              <div className="tracker-table-vertical-wrap">
                <div className="mobile-table-scroll-hint">
                  <span>Swipe horizontally to view all columns &rarr;</span>
                </div>
                <Table hover className="tracker-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: "120px" }}>Timestamp</th>
                      <th style={{ width: "140px" }}>Event Type</th>
                      <th>Event Title</th>
                      <th>Details & Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          No activity logs found matching current filter.
                        </td>
                      </tr>
                    ) : (
                      paginatedLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="small font-monospace text-muted text-nowrap">{log.timeStr}</td>
                          <td>
                            {renderEventBadge(log.type)}
                          </td>
                          <td className="fw-bold small text-dark">{log.title}</td>
                          <td className="small text-muted">{log.details}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>

            <Card.Footer className="tracker-pagination-footer">
              <Row className="align-items-center g-2 tracker-pagination-row">
                <Col xs={12} lg={6} className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-2 tracker-pagination-info">
                  <span className="small text-muted fw-bold">Show</span>
                  <Form.Select
                    size="sm"
                    style={{ width: "75px" }}
                    value={logsPageSize}
                    onChange={(e) => {
                      setLogsPageSize(Number(e.target.value));
                      setLogsPage(1);
                    }}
                    className="fw-semibold"
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </Form.Select>
                  <span className="small text-muted fw-bold">Entries per page</span>
                  <span className="small text-muted ms-md-3">
                    Showing <strong>{logsStartIndex}</strong> to <strong>{logsEndIndex}</strong> of{" "}
                    <strong>{filteredLogs.length}</strong> entries
                  </span>
                </Col>
                <Col xs={12} lg={6} className="d-flex justify-content-center justify-content-lg-end align-items-center mt-2 mt-lg-0">
                  <Pagination size="sm" className="mb-0 flex-wrap justify-content-center">
                    <Pagination.First
                      disabled={logsPage === 1}
                      onClick={() => setLogsPage(1)}
                    />
                    <Pagination.Prev
                      disabled={logsPage === 1}
                      onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                    />
                    {renderPaginationButtons(logsPage, totalLogsPages, setLogsPage)}
                    <Pagination.Next
                      disabled={logsPage === totalLogsPages || totalLogsPages === 0}
                      onClick={() => setLogsPage((p) => Math.min(totalLogsPages, p + 1))}
                    />
                    <Pagination.Last
                      disabled={logsPage === totalLogsPages || totalLogsPages === 0}
                      onClick={() => setLogsPage(totalLogsPages)}
                    />
                  </Pagination>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        )}

        {/* =========================================================================
            TAB 4: BREAK HISTORY
           ========================================================================= */}
        {activeTab === "breaks" && (
          <>
            {/* Break Summary Cards */}
            <Row className="g-3 mb-4">
              <Col xs={12} sm={4}>
                <Card className="shadow-sm border-0 bg-white">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="tracker-stat-icon-wrap stat-icon-inactive">
                        <FontAwesomeIcon icon={faCoffee} />
                      </div>
                      <div>
                        <div className="tracker-stat-label">Tea Breaks</div>
                        <div className="fs-4 fw-bold text-dark">
                          {filteredBreaks.filter((b) => b.type === "Tea Break").length} Taken
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} sm={4}>
                <Card className="shadow-sm border-0 bg-white">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="tracker-stat-icon-wrap stat-icon-active">
                        <FontAwesomeIcon icon={faUtensils} />
                      </div>
                      <div>
                        <div className="tracker-stat-label">Lunch Breaks</div>
                        <div className="fs-4 fw-bold text-dark">
                          {filteredBreaks.filter((b) => b.type === "Lunch Break").length} Taken
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12} sm={4}>
                <Card className="shadow-sm border-0 bg-white">
                  <Card.Body className="p-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="tracker-stat-icon-wrap stat-icon-break">
                        <FontAwesomeIcon icon={faUserClock} />
                      </div>
                      <div>
                        <div className="tracker-stat-label">Personal Breaks</div>
                        <div className="fs-4 fw-bold text-dark">
                          {filteredBreaks.filter((b) => b.type === "Personal Break").length} Taken
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Breaks Table */}
            <Card className="shadow-sm border-0">
              <Card.Header className="bg-white border-bottom py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <h5 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
                    <FontAwesomeIcon icon={faCoffee} className="text-warning" />
                    <span>Detailed Break Records</span>
                  </h5>
                  <small className="text-muted">
                    Showing <strong>{filteredBreaks.length}</strong> breaks matching current filter
                  </small>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2 tracker-table-toolbar">
                  {/* Search */}
                  <InputGroup size="sm" className="tracker-filter-search" style={{ maxWidth: "180px" }}>
                    <InputGroup.Text className="bg-light border-end-0">
                      <FontAwesomeIcon icon={faSearch} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search breaks..."
                      className="border-start-0"
                      value={breakSearch}
                      onChange={(e) => setBreakSearch(e.target.value)}
                    />
                  </InputGroup>

                  {/* Date / Duration filter */}
                  <Form.Select
                    size="sm"
                    className="tracker-filter-select"
                    style={{ maxWidth: "165px" }}
                    value={breakDateFilter}
                    onChange={(e) => setBreakDateFilter(e.target.value)}
                  >
                    <option value="ALL">All Dates</option>
                    <option value="TODAY">Today</option>
                    <option value="YESTERDAY">Yesterday</option>
                    <option value="LAST_7_DAYS">Last 7 Days (Duration)</option>
                    <option value="LAST_30_DAYS">Last 30 Days (Duration)</option>
                    <option value="THIS_MONTH">This Month (Duration)</option>
                    <option value="SINGLE_DATE">Specific Date...</option>
                    <option value="CUSTOM_RANGE">Custom Duration (From - To)...</option>
                  </Form.Select>

                  {breakDateFilter === "SINGLE_DATE" && (
                    <Form.Control
                      type="date"
                      size="sm"
                      className="tracker-filter-select"
                      style={{ maxWidth: "140px" }}
                      value={breakSingleDate}
                      onChange={(e) => setBreakSingleDate(e.target.value)}
                    />
                  )}

                  {breakDateFilter === "CUSTOM_RANGE" && (
                    <div className="d-flex align-items-center gap-1">
                      <Form.Control
                        type="date"
                        size="sm"
                        style={{ maxWidth: "135px" }}
                        value={breakStartDate}
                        onChange={(e) => setBreakStartDate(e.target.value)}
                        title="From Date"
                      />
                      <span className="small text-muted">to</span>
                      <Form.Control
                        type="date"
                        size="sm"
                        style={{ maxWidth: "135px" }}
                        value={breakEndDate}
                        onChange={(e) => setBreakEndDate(e.target.value)}
                        title="To Date"
                      />
                    </div>
                  )}

                  {/* Export Breaks CSV button */}
                  <Button
                    variant="outline-info"
                    size="sm"
                    className="d-flex align-items-center justify-content-center gap-2 fw-bold tracker-filter-btn"
                    onClick={() => exportBreaksCSV(filteredBreaks)}
                  >
                    <FontAwesomeIcon icon={faFileCsv} />
                    <span>Export CSV</span>
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                <div className="tracker-table-vertical-wrap">
                  <div className="mobile-table-scroll-hint">
                    <span>Swipe horizontally to view all columns &rarr;</span>
                  </div>
                  <Table hover className="tracker-table mb-0">
                    <thead>
                      <tr>
                        <th>Break ID</th>
                        <th>Type</th>
                        <th>Reason</th>
                        <th>Date</th>
                        <th>Start Time</th>
                        <th>End Time</th>
                        <th>Duration</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {breakHistory.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4 text-muted">
                            No breaks logged yet.
                          </td>
                        </tr>
                      ) : (
                        paginatedBreaks.map((b, idx) => (
                          <tr key={b?.id ? `${b.id}-${idx}` : `brk-page-${idx}`}>
                            <td className="font-monospace small text-muted">{b.id}</td>
                            <td className="fw-bold text-dark">
                              <FontAwesomeIcon
                                icon={
                                  b.type.includes("Lunch")
                                    ? faUtensils
                                    : b.type.includes("Personal")
                                      ? faUserClock
                                      : faCoffee
                                }
                                className={`me-2 ${b.type.includes("Lunch")
                                  ? "text-primary"
                                  : b.type.includes("Personal")
                                    ? "text-info"
                                    : "text-warning"
                                  }`}
                              />
                              {b.type}
                            </td>
                            <td>
                              <span className="badge bg-light text-dark border">
                                {b.reason || b.type}
                              </span>
                            </td>
                            <td>{b.date}</td>
                            <td className="small text-muted">{b.startTime}</td>
                            <td className="small text-muted">{b.endTime}</td>
                            <td className="fw-bold text-primary font-monospace">{formatBreakTimeMinutes(b.durationSeconds || b.durationFormatted)}</td>
                            <td>
                              <Badge bg="success">{b.status}</Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>

              <Card.Footer className="tracker-pagination-footer">
                <Row className="align-items-center g-2 tracker-pagination-row">
                  <Col xs={12} lg={6} className="d-flex flex-wrap align-items-center justify-content-center justify-content-lg-start gap-2 tracker-pagination-info">
                    <span className="small text-muted fw-bold">Show</span>
                    <Form.Select
                      size="sm"
                      style={{ width: "75px" }}
                      value={breaksPageSize}
                      onChange={(e) => {
                        setBreaksPageSize(Number(e.target.value));
                        setBreaksPage(1);
                      }}
                      className="fw-semibold"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Form.Select>
                    <span className="small text-muted fw-bold">Entries per page</span>
                    <span className="small text-muted ms-md-3">
                      Showing <strong>{breaksStartIndex}</strong> to <strong>{breaksEndIndex}</strong> of{" "}
                      <strong>{filteredBreaks.length}</strong> entries
                    </span>
                  </Col>
                  <Col xs={12} lg={6} className="d-flex justify-content-center justify-content-lg-end align-items-center mt-2 mt-lg-0">
                    <Pagination size="sm" className="mb-0 flex-wrap justify-content-center">
                      <Pagination.First
                        disabled={breaksPage === 1}
                        onClick={() => setBreaksPage(1)}
                      />
                      <Pagination.Prev
                        disabled={breaksPage === 1}
                        onClick={() => setBreaksPage((p) => Math.max(1, p - 1))}
                      />
                      {renderPaginationButtons(breaksPage, totalBreaksPages, setBreaksPage)}
                      <Pagination.Next
                        disabled={breaksPage === totalBreaksPages || totalBreaksPages === 0}
                        onClick={() => setBreaksPage((p) => Math.min(totalBreaksPages, p + 1))}
                      />
                      <Pagination.Last
                        disabled={breaksPage === totalBreaksPages || totalBreaksPages === 0}
                        onClick={() => setBreaksPage(totalBreaksPages)}
                      />
                    </Pagination>
                  </Col>
                </Row>
              </Card.Footer>
            </Card>
          </>
        )}

        {/* =========================================================================
            TAB 5: MY TASKS & PROJECT MANAGEMENT
           ========================================================================= */}
        {activeTab === "tasks" && (
          <EmployeeTaskManagement currentEmployee={employee} />
        )}

      </Container>

      {/* =========================================================================
          MODALS
         ========================================================================= */}

      {/* 0. DEDICATED PERSONAL BREAK REASON MODAL (OPENS ON CLICKING "START PERSONAL BREAK") */}
      <Modal
        show={showPersonalBreakModal}
        onHide={() => setShowPersonalBreakModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header className="tracker-modal-header" closeButton>
          <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center rounded-circle"
              style={{ width: "36px", height: "36px", background: "rgba(13, 202, 240, 0.15)", color: "#0dcaf0" }}
            >
              <FontAwesomeIcon icon={faUserClock} />
            </div>
            <div>
              <div className="text-white">Personal Break</div>
              <div className="small text-muted fw-normal" style={{ fontSize: "0.78rem" }}>
                Enter the reason for your personal break
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="p-4">
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold text-dark d-flex justify-content-between align-items-center mb-1">
              <span>
                Enter Reason of Break: <span className="text-danger">*</span>
              </span>

            </Form.Label>
            <Form.Control
              autoFocus
              type="text"
              size="lg"
              placeholder="Enter a reason ..."
              value={personalBreakReason}
              onChange={(e) => setPersonalBreakReason(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleConfirmPersonalBreak();
                }
              }}
              maxLength={100}
            />

          </Form.Group>

          {/* Quick Suggestions Chips */}
          <div className="mb-3">
            <div className="small text-muted fw-bold mb-2">Quick suggestions:</div>
            <div className="d-flex flex-wrap gap-2">
              {[
                "Doctor Appointment",
                "Important Phone Call",
                "Bank Work",
                "Prayer Time",
                "Family Emergency",
                "Rest / Eye Break",
                "Courier / Delivery",
                "Personal Errand",
              ].map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className={`btn btn-sm rounded-pill px-3 py-1 ${personalBreakReason === chip
                    ? "btn-info text-white shadow-sm fw-bold"
                    : "btn-outline-secondary"
                    }`}
                  style={{ fontSize: "0.8rem", transition: "all 0.15s ease-in-out" }}
                  onClick={() => setPersonalBreakReason(chip)}
                >
                  {chip}
                </button>
              ))}
            </div>
          </div>

          {/* Expected Duration (User's choice) */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-dark mb-1">
              Expected Duration (User&apos;s Choice):
            </Form.Label>
            <Form.Select
              size="sm"
              value={personalBreakDuration}
              onChange={(e) => setPersonalBreakDuration(e.target.value)}
            >
              <option value="5 min">5 Minutes (Quick)</option>
              <option value="10 min">10 Minutes</option>
              <option value="15 min">15 Minutes</option>
              <option value="20 min">20 Minutes</option>
              <option value="30 min">30 Minutes</option>
              <option value="45 min">45 Minutes</option>
              <option value="60 min">1 Hour</option>
            </Form.Select>
          </Form.Group>

          <div className="p-2 rounded bg-light border text-muted small" style={{ fontSize: "0.75rem" }}>
            Activity tracking pauses during break and automatically finishes when the duration completes (or click <strong>End Break</strong> anytime to resume earlier).
          </div>
        </Modal.Body>

        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowPersonalBreakModal(false)}>
            Cancel
          </Button>
          <Button
            variant="info"
            disabled={isOnBreak || !isShiftActive || isStartingBreak}
            className="text-white fw-bold d-flex align-items-center gap-2 px-4 shadow-sm"
            onClick={handleConfirmPersonalBreak}
          >
            {isStartingBreak ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FontAwesomeIcon icon={faPlay} />
            )}
            <span>{isStartingBreak ? "Connecting Server..." : "Confirm & Start Personal Break"}</span>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 1. START BREAK MODAL */}
      <Modal show={showBreakModal} onHide={() => setShowBreakModal(false)} centered>
        <Modal.Header className="tracker-modal-header" closeButton>
          <Modal.Title className="fs-5 fw-bold d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faCoffee} className="text-warning" />
            Take a Break
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="text-muted small mb-3">
            Choose your break type below. Activity tracking will pause, and your status will change to
            <strong> On Break</strong>.
          </p>

          <Row className="g-2 mb-3">
            <Col xs={4}>
              <div
                className={`break-select-card ${isTeaActive
                  ? "selected active-running-break"
                  : selectedBreakType === "Tea Break" && !isOnBreak
                    ? "selected"
                    : ""
                  } ${isTeaDisabled ? "break-card-disabled" : ""}`}
                onClick={() => !isTeaDisabled && handleSelectBreakType("Tea Break")}
              >
                <div className="break-card-icon text-warning">
                  <FontAwesomeIcon icon={faCoffee} />
                </div>
                <div className="fw-bold small text-dark">Tea Break</div>
                {isTeaActive ? (
                  <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.68rem" }}>
                    Active Now
                  </Badge>
                ) : isOnBreak ? (
                  <Badge bg="secondary" className="mt-1" style={{ fontSize: "0.65rem" }}>
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Disabled
                  </Badge>
                ) : (
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    ~15 min
                  </div>
                )}
              </div>
            </Col>

            <Col xs={4}>
              <div
                className={`break-select-card ${isLunchActive
                  ? "selected active-running-break"
                  : selectedBreakType === "Lunch Break" && !isOnBreak
                    ? "selected"
                    : ""
                  } ${isLunchDisabled ? "break-card-disabled" : ""}`}
                onClick={() => !isLunchDisabled && handleSelectBreakType("Lunch Break")}
              >
                <div className="break-card-icon text-primary">
                  <FontAwesomeIcon icon={faUtensils} />
                </div>
                <div className="fw-bold small text-dark">Lunch Break</div>
                {isLunchActive ? (
                  <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.68rem" }}>
                    Active Now
                  </Badge>
                ) : isOnBreak ? (
                  <Badge bg="secondary" className="mt-1" style={{ fontSize: "0.65rem" }}>
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Disabled
                  </Badge>
                ) : (
                  <div className="text-muted" style={{ fontSize: "0.75rem" }}>
                    ~30 min
                  </div>
                )}
              </div>
            </Col>

            <Col xs={4}>
              <div
                className={`break-select-card ${isPersonalActive
                  ? "selected active-running-break"
                  : selectedBreakType === "Personal Break" && !isOnBreak
                    ? "selected"
                    : ""
                  } ${isPersonalDisabled ? "break-card-disabled" : ""}`}
                onClick={() => !isPersonalDisabled && handleSelectBreakType("Personal Break")}
              >
                <div className="break-card-icon text-info">
                  <FontAwesomeIcon icon={faUserClock} />
                </div>
                <div className="fw-bold small text-dark">Personal</div>
                {isPersonalActive ? (
                  <Badge bg="warning" text="dark" className="mt-1" style={{ fontSize: "0.68rem" }}>
                    Active Now
                  </Badge>
                ) : isOnBreak ? (
                  <Badge bg="secondary" className="mt-1" style={{ fontSize: "0.65rem" }}>
                    <FontAwesomeIcon icon={faLock} className="me-1" />
                    Disabled
                  </Badge>
                ) : (
                  <div className="text-info fw-bold" style={{ fontSize: "0.75rem" }}>
                    User's Choice
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* BREAK REASON INPUT - USERS CAN ENTER REASON */}
          <div className="p-3 border rounded bg-light mb-2">
            <Form.Group className="mb-2">
              <Form.Label className="small fw-bold text-dark d-flex justify-content-between align-items-center mb-1">
                <span>
                  Enter Reason of Break: <span className="text-danger">*</span>
                </span>
                <span className="badge bg-primary text-white" style={{ fontSize: "0.72rem" }}>
                  {selectedBreakType}
                </span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder={
                  selectedBreakType === "Personal Break"
                    ? "Enter your custom reason (e.g. Doctor appointment, Important call, Bank work)..."
                    : `Enter reason / note for ${selectedBreakType}...`
                }
                value={breakReason}
                onChange={(e) => setBreakReason(e.target.value)}
                maxLength={80}
              />
            </Form.Group>

            {/* Quick Suggestion Chips based on selected break type */}
            <div className="d-flex flex-wrap align-items-center gap-1 mt-2">
              <span className="text-muted small me-1" style={{ fontSize: "0.72rem" }}>
                Quick suggestions:
              </span>
              {(selectedBreakType === "Tea Break"
                ? ["Morning Tea & Refreshment", "Evening Coffee", "Quick Tea Break", "Snacks Break"]
                : selectedBreakType === "Lunch Break"
                  ? ["Lunch Break", "Afternoon Meal", "Lunch & Rest", "Dining Outside"]
                  : [
                    "Doctor Appointment",
                    "Important Phone Call",
                    "Bank / Work Errand",
                    "Prayer Time",
                    "Family Emergency",
                    "Rest / Eye Break",
                  ]
              ).map((chip) => (
                <button
                  key={chip}
                  type="button"
                  className={`btn btn-xs py-0 px-2 rounded-pill ${breakReason === chip
                    ? "btn-primary text-white"
                    : "btn-outline-secondary"
                    }`}
                  style={{ fontSize: "0.72rem" }}
                  onClick={() => setBreakReason(chip)}
                >
                  {chip}
                </button>
              ))}
            </div>

            <Form.Group className="mt-3 mb-0">
              <Form.Label className="small fw-bold text-dark mb-1 d-flex justify-content-between align-items-center">
                <span>Expected Break Duration:</span>
                <span className="text-muted" style={{ fontSize: "0.72rem" }}>
                  {selectedBreakType === "Tea Break" ? "Standard: 15 min" : selectedBreakType === "Lunch Break" ? "Standard: 30 min" : "User's Choice"}
                </span>
              </Form.Label>
              <Form.Select
                size="sm"
                value={personalBreakDuration}
                onChange={(e) => setPersonalBreakDuration(e.target.value)}
              >
                <option value="5 min">5 Minutes (Quick)</option>
                <option value="10 min">10 Minutes</option>
                <option value="15 min">15 Minutes</option>
                <option value="20 min">20 Minutes</option>
                <option value="30 min">30 Minutes</option>
                <option value="45 min">45 Minutes</option>
                <option value="60 min">1 Hour</option>
              </Form.Select>
            </Form.Group>

            <div className="text-muted mt-2" style={{ fontSize: "0.74rem" }}>
              Activity tracking pauses during break and countdown will automatically resume when the scheduled duration completes (or click End Break anytime).
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="bg-light">
          <Button variant="secondary" onClick={() => setShowBreakModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isOnBreak || !isShiftActive || isStartingBreak}
            className="fw-bold d-flex align-items-center gap-2"
            onClick={handleStartBreakSubmit}
          >
            {isStartingBreak ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FontAwesomeIcon icon={faPlay} />
            )}
            <span>{isStartingBreak ? "Connecting Server..." : "Confirm Start Break"}</span>
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 2. SHIFT END CONFIRMATION MODAL */}
      <Modal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        centered
      >
        <Modal.Body className="p-4 text-center">


          <h5 className="fw-bold text-dark mb-2">
            {isShiftEndedToday ? "Record / Update Shift End?" : "End Today's Shift?"}
          </h5>

          <p className="text-muted small mb-3 px-2">
            {isShiftEndedToday ? (
              <span>You can re-confirm or update your final shift end time on the server for today whenever you want.</span>
            ) : (
              <>
                Please do not end your shift unless you are done for the day.
                <br />
                <span className="text-danger fw-semibold">Once ended, today's shift cannot be resumed.</span>
              </>
            )}
          </p>

          <div className="bg-light border rounded-3 p-2 mb-3 d-flex justify-content-around text-center">
            <div>
              <div className="text-muted text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>Active Work</div>
              <div className="fw-bold text-success font-monospace fs-6">{formatDurationHuman(activeSeconds)}</div>
            </div>
            <div className="border-start"></div>
            <div>
              <div className="text-muted text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}>Total Session</div>
              <div className="fw-bold text-primary font-monospace fs-6">{formatDurationHuman(sessionSeconds)}</div>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-center pt-2">
            <Button
              variant="outline-secondary"
              className="px-3 py-2 fw-bold"
              onClick={() => setShowLogoutModal(false)}
            >
              {isShiftEndedToday ? "Cancel" : "Cancel, Keep Working"}
            </Button>
            <Button
              variant="danger"
              className="px-3 py-2 fw-bold d-flex align-items-center gap-2 shadow-sm"
              disabled={isEndingShift}
              onClick={handleEndShiftStayOnPage}
            >
              {isEndingShift ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <FontAwesomeIcon icon={faStop} />
              )}
              <span>{isEndingShift ? "Recording Shift End..." : isShiftEndedToday ? "Record End Shift Now" : "Yes, End Shift"}</span>
            </Button>
          </div>
        </Modal.Body>
      </Modal>



      {/* 3. ATTENDANCE RECORD DETAIL MODAL */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered>
        <Modal.Header className="tracker-modal-header" closeButton>
          <Modal.Title className="fs-5 fw-bold text-white">Attendance Record Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedAttendanceRecord && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 text-dark fw-bold">{selectedAttendanceRecord.date}</h5>
                <Badge
                  bg={
                    selectedAttendanceRecord.status === "Completed"
                      ? "success"
                      : selectedAttendanceRecord.status === "In Progress"
                        ? "primary"
                        : selectedAttendanceRecord.status === "Half-Day"
                          ? "warning"
                          : "secondary"
                  }
                >
                  {selectedAttendanceRecord.status}
                </Badge>
              </div>

              <div className="p-3 border rounded bg-light mb-3">
                <div className="row g-2 small">
                  <div className="col-12 col-sm-6">
                    <span className="text-muted">Username:</span>{" "}
                    <strong>{selectedAttendanceRecord.deskusername || employee.userName || employee.name}</strong>
                  </div>
                  <div className="col-12 col-sm-6">
                    <span className="text-muted">Record ID:</span>{" "}
                    <strong className="font-monospace">{selectedAttendanceRecord.id}</strong>
                  </div>
                  <div className="col-12 col-sm-6">
                    <span className="text-muted">Login Time:</span>{" "}
                    <strong>{selectedAttendanceRecord.loginTime}</strong>
                  </div>
                  <div className="col-12 col-sm-6">
                    <span className="text-muted">Logout Time:</span>{" "}
                    <strong>{selectedAttendanceRecord.logoutTime}</strong>
                  </div>
                </div>
              </div>

              <div className="list-group list-group-flush border-top border-bottom small">
                {/* <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                  <span className="text-muted">Screen Lock Time (desktotalScreenlocktime)</span>
                  <span className="fw-bold text-warning font-monospace">
                    {formatDuration(
                      selectedAttendanceRecord.desktotalScreenlocktime !== undefined && selectedAttendanceRecord.desktotalScreenlocktime !== null && selectedAttendanceRecord.desktotalScreenlocktime !== ""
                        ? selectedAttendanceRecord.desktotalScreenlocktime
                        : (selectedAttendanceRecord.screenLockSeconds || 0)
                    )}
                  </span>
                </div> */}
                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                  <span className="text-muted">Idle Time (desktotalidletime)</span>
                  <span className="fw-bold text-secondary font-monospace">
                    {formatDuration(
                      selectedAttendanceRecord.desktotalidletime !== undefined && selectedAttendanceRecord.desktotalidletime !== null && selectedAttendanceRecord.desktotalidletime !== ""
                        ? selectedAttendanceRecord.desktotalidletime
                        : (selectedAttendanceRecord.idleSeconds || selectedAttendanceRecord.inactiveSeconds || 0)
                    )}
                  </span>
                </div>
                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                  <span className="text-muted">Total Break Time (breaktime)</span>
                  <span className="fw-bold text-primary font-monospace">
                    {formatBreakTimeMinutes(
                      selectedAttendanceRecord.breaktime !== undefined && selectedAttendanceRecord.breaktime !== null && selectedAttendanceRecord.breaktime !== ""
                        ? selectedAttendanceRecord.breaktime
                        : (selectedAttendanceRecord.breakSeconds || 0)
                    )}
                  </span>
                </div>
                <div className="list-group-item d-flex justify-content-between align-items-center px-0">
                  <span className="text-muted">Active Time (screenactivetime)</span>
                  <span className="fw-bold text-success font-monospace">
                    {formatDuration(
                      parseScreenActiveTimeToSeconds(
                        selectedAttendanceRecord.screenactivetime !== undefined && selectedAttendanceRecord.screenactivetime !== null && selectedAttendanceRecord.screenactivetime !== ""
                          ? selectedAttendanceRecord.screenactivetime
                          : (selectedAttendanceRecord.screenActiveTime || selectedAttendanceRecord.activeSeconds || 0)
                      )
                    )}
                    {" "}
                    <small className="text-muted fw-normal ms-1">
                      ({formatDurationHuman(
                        parseScreenActiveTimeToSeconds(
                          selectedAttendanceRecord.screenactivetime !== undefined && selectedAttendanceRecord.screenactivetime !== null && selectedAttendanceRecord.screenactivetime !== ""
                            ? selectedAttendanceRecord.screenactivetime
                            : (selectedAttendanceRecord.screenActiveTime || selectedAttendanceRecord.activeSeconds || 0)
                        )
                      )})
                    </small>
                  </span>
                </div>
                <div className="list-group-item d-flex justify-content-between align-items-center px-0 bg-light">
                  <span className="fw-bold text-dark">Total Shift Duration</span>
                  <span className="fw-bold text-dark fs-6 font-monospace">
                    {formatDuration(selectedAttendanceRecord.totalSeconds || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

      </Modal>

      {/* FLOATING TOAST NOTIFICATIONS */}
      <div className="tracker-toast-container">
        {toasts.map((toast) => (
          <div key={toast.id} className={`tracker-toast-item toast-${toast.type}`}>
            <div className="fs-5 text-primary">
              {toast.type === "success" && <FontAwesomeIcon icon={faCheckCircle} className="text-success" />}
              {toast.type === "warning" && <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning" />}
              {toast.type === "info" && <FontAwesomeIcon icon={faDesktop} className="text-info" />}
              {toast.type === "danger" && <FontAwesomeIcon icon={faTimes} className="text-danger" />}
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold text-dark small">{toast.message}</div>
              {toast.description && <div className="text-muted small">{toast.description}</div>}
            </div>
            <button
              type="button"
              className="btn-close btn-sm ms-auto"
              style={{ fontSize: "0.65rem" }}
              onClick={() => dismissToast(toast.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Export Component wrapped in Provider and connected to Redux for activateGeod
const EmployeeActivityTracker = (props) => {
  return (
    <TaskManagementProvider>
      <ActivityTrackerProvider>
        <EmployeeActivityTrackerContent {...props} />
      </ActivityTrackerProvider>
    </TaskManagementProvider>
  );
};

const mapStateToProps = (state) => ({
  geod: state.geod,
});

const mapDispatchToProps = {
  activateGeod,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeActivityTracker);
